import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import SearchableSelect from "./SearchableSelect";
import { EyeOff, Eye } from "lucide-react";

// Lightweight in-memory cache so the picker doesn't refetch on every modal open
let _cache = null;
let _inflight = null;

async function loadEmployees() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/employees").then(({ data }) => {
    _cache = data || [];
    _inflight = null;
    return _cache;
  }).catch(() => {
    _inflight = null;
    return [];
  });
  return _inflight;
}

export function invalidateEmployeeCache() { _cache = null; }

/**
 * EmployeePicker — searchable employee dropdown.
 * Default: hides "abgemeldete" (status === "inaktiv") employees from the list.
 * The currently selected value is ALWAYS shown, even if inactive, so editing
 * historic records keeps working.
 *
 * Footer toggle "Auch abgemeldete anzeigen" lets the user temporarily reveal
 * inactive employees within the open dropdown.
 */
export default function EmployeePicker({
  value,
  onChange,
  placeholder = "— Mitarbeiter wählen —",
  testid = "employee-picker",
  disabled = false,
  filter, // optional (e) => bool
  includeInactive = false,
}) {
  const [emps, setEmps] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => { loadEmployees().then(setEmps); }, []);

  const effectiveShowInactive = includeInactive || showInactive;
  const inactiveCount = useMemo(() =>
    emps.filter((e) => e.status === "inaktiv" && (filter ? filter(e) : true)).length,
    [emps, filter]
  );

  const options = emps
    .filter((e) => effectiveShowInactive || e.status !== "inaktiv" || e.id === value)
    .filter((e) => (filter ? filter(e) : true))
    .map((e) => ({
      value: e.id,
      label: `${e.first_name} ${e.last_name}`,
      hint: e.status === "inaktiv" ? "abgemeldet" : (e.role || ""),
    }));

  const footer = inactiveCount > 0 && !includeInactive ? (
    <button
      type="button"
      onClick={() => setShowInactive((s) => !s)}
      data-testid={`${testid}-toggle-inactive`}
      className="w-full inline-flex items-center justify-center gap-2 px-2 py-1 text-[11px] rounded text-zinc-300 hover:bg-zinc-800 hover:text-white transition"
    >
      {showInactive ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      {showInactive
        ? `Abgemeldete ausblenden (${inactiveCount})`
        : `Auch abgemeldete anzeigen (${inactiveCount})`}
    </button>
  ) : null;

  return (
    <SearchableSelect
      options={options}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      testid={testid}
      disabled={disabled}
      footer={footer}
    />
  );
}
